// ============================================================
// pagoPage.js — Página de pago / checkout
// ============================================================
import { qs, qsa, on } from './utils.js';
import { toast } from './toast.js';

const PLANS = {
  suscripcion: {
    name: 'Suscripción Vimo',
    priceMonth: { ES_LATAM: 29.90, BR: 19.90 },
    priceYear: { ES_LATAM: 23.90, BR: 15.90 },
    currency: { ES_LATAM: '$', BR: '$' },
    features: [
      'Generación de vídeos ilimitada',
      'Generación de imágenes con IA de alta calidad',
      'Acceso completo a avatares HeyGen',
      'Clonación de voz ultra-realista con ElevenLabs',
      'Resolución en alta definición (HD/4K)',
      'Acceso prioritario a nuevos modelos',
      'Soporte premium 24/7',
    ],
  },
};

const VAT = 0.21;

export function initPagoPage() {
  if (document.body.dataset.page !== 'pago') return;

  let billing = 'month';

  const plan = PLANS.suscripcion;

  // Detectar la región del usuario logueado
  const user = JSON.parse(localStorage.getItem('vimo_user') || '{}');
  const region = user.region || 'ES_LATAM';

  // Plan name (sidebar on desktop + mobile header in form section)
  [qs('#pagoSummaryPlan'), qs('#pagoSummaryPlanMobile')].forEach(el => {
    if (el) el.textContent = plan.name;
  });

  // Features list (sidebar on desktop + mobile extras in form section)
  const featuresHtml = plan.features
    .map(f => `<li class="pago-includes-item"><i class="bi bi-check-lg" aria-hidden="true"></i>${f}</li>`)
    .join('');
  const featuresList = qs('#pagoFeaturesList');
  if (featuresList) featuresList.innerHTML = featuresHtml;
  const featuresListMobile = qs('#pagoFeaturesListMobile');
  if (featuresListMobile) featuresListMobile.innerHTML = featuresHtml;

  function updatePrices() {
    const currency = plan.currency[region] || '$';
    const priceMonth = plan.priceMonth[region] || plan.priceMonth.ES_LATAM;

    const base    = priceMonth;
    // En Brasil no se aplica el IVA del 21%
    const vatRate = region === 'BR' ? 0 : VAT;
    const vat     = base * vatRate;
    const total   = base + vat;
    const fmt     = n => n.toFixed(2).replace('.', ',') + ' ' + currency;
    const zero    = '0,00 ' + currency;

    const labelEl    = qs('#pagoPriceLabel');
    const strikeEl   = qs('#pagoPriceStrike');
    const baseEl     = qs('#pagoPriceBase');
    const vatEl      = qs('#pagoVatAmount');
    const totalEl    = qs('#pagoTotalAmount');
    const discountEl = qs('#pagoDiscountBadge');
    const afterEl        = qs('#pagoTrialAfter');
    const afterShortEl   = qs('#pagoTrialAfterShort');
    const afterMobileEl  = qs('#pagoTrialAfterMobile');

    if (labelEl)   labelEl.textContent  = `${fmt(priceMonth)} al mes`;
    if (strikeEl)  strikeEl.textContent = fmt(base);
    if (baseEl)    baseEl.textContent   = zero;
    if (vatEl)     vatEl.textContent    = zero;
    if (totalEl)   totalEl.textContent  = zero;
    if (discountEl) discountEl.hidden   = true;
    const afterText = `${fmt(base)}/mes`;
    if (afterEl)       afterEl.textContent       = afterText;
    if (afterShortEl)  afterShortEl.textContent  = afterText;
    if (afterMobileEl) afterMobileEl.textContent = afterText;
  }

  updatePrices();

  // Payment method tabs
  let activeMethod = 'card';
  on(document, 'click', '.pago-method-tab', (_e, tab) => {
    activeMethod = tab.dataset.tab;
    qsa('.pago-method-tab').forEach(t => t.classList.remove('is-active'));
    tab.classList.add('is-active');

    qsa('.pago-panel').forEach(p => p.classList.add('is-hidden'));
    const id = 'pagoTab' + activeMethod.charAt(0).toUpperCase() + activeMethod.slice(1);
    qs('#' + id)?.classList.remove('is-hidden');
  });

  // Inputs de tarjeta y formateo automático
  const cardNameInput   = qs('input[name="cardName"]');
  const cardNumberInput = qs('input[name="cardNumber"]');
  const cardExpiryInput = qs('input[name="cardExpiry"]');
  const cardCvvInput    = qs('input[name="cardCvv"]');

  [cardNameInput, cardNumberInput, cardExpiryInput, cardCvvInput].forEach(inp => {
    inp?.addEventListener('input', () => {
      inp.classList.remove('is-invalid');
    });
  });

  if (cardNumberInput) {
    cardNumberInput.addEventListener('input', (e) => {
      let val = e.target.value.replace(/\D/g, '').substring(0, 16);
      let formatted = val.match(/.{1,4}/g)?.join(' ') || val;
      e.target.value = formatted;
    });
  }

  if (cardExpiryInput) {
    cardExpiryInput.addEventListener('input', (e) => {
      let val = e.target.value.replace(/\D/g, '').substring(0, 4);
      if (val.length >= 3) {
        e.target.value = val.substring(0, 2) + '/' + val.substring(2);
      } else {
        e.target.value = val;
      }
    });
  }

  if (cardCvvInput) {
    cardCvvInput.addEventListener('input', (e) => {
      e.target.value = e.target.value.replace(/\D/g, '').substring(0, 4);
    });
  }

  // Form submit con validación estricta de datos bancarios
  qs('[data-pago-form]')?.addEventListener('submit', e => {
    e.preventDefault();

    if (activeMethod === 'card') {
      const nameVal = cardNameInput?.value?.trim() || '';
      const numVal  = (cardNumberInput?.value || '').replace(/\s+/g, '');
      const expVal  = (cardExpiryInput?.value || '').trim();
      const cvvVal  = (cardCvvInput?.value || '').trim();

      if (!nameVal || nameVal.length < 3) {
        cardNameInput?.classList.add('is-invalid');
        cardNameInput?.focus();
        toast({ message: 'Por favor, introduce el nombre del titular de la tarjeta.', type: 'error' });
        return;
      }

      if (!/^\d{15,19}$/.test(numVal)) {
        cardNumberInput?.classList.add('is-invalid');
        cardNumberInput?.focus();
        toast({ message: 'Por favor, introduce un número de tarjeta válido (16 dígitos).', type: 'error' });
        return;
      }

      const expMatch = expVal.match(/^(0[1-9]|1[0-2])\/(\d{2})$/);
      if (!expMatch) {
        cardExpiryInput?.classList.add('is-invalid');
        cardExpiryInput?.focus();
        toast({ message: 'Introduce una fecha de caducidad válida en formato MM/AA.', type: 'error' });
        return;
      }

      const expMonth = parseInt(expMatch[1], 10);
      const expYear  = 2000 + parseInt(expMatch[2], 10);
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;

      if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
        cardExpiryInput?.classList.add('is-invalid');
        cardExpiryInput?.focus();
        toast({ message: 'La tarjeta indicada está caducada.', type: 'error' });
        return;
      }

      if (!/^\d{3,4}$/.test(cvvVal)) {
        cardCvvInput?.classList.add('is-invalid');
        cardCvvInput?.focus();
        toast({ message: 'Introduce un código de seguridad CVV válido (3 o 4 dígitos).', type: 'error' });
        return;
      }
    } else if (activeMethod === 'paypal') {
      toast({ message: 'Redirigiendo a PayPal para autorizar el método de pago...', type: 'info' });
    } else if (activeMethod === 'googlepay') {
      toast({ message: 'Conectando con Google Pay...', type: 'info' });
    }

    // Verificar si el usuario está autenticado
    const token = localStorage.getItem('vimo_token');
    if (!token) {
      toast({ message: 'Debes iniciar sesión o crear tu cuenta para vincular tu suscripción.', type: 'warning' });
      setTimeout(() => {
        window.location.href = 'registro.html?redirect=pago.html';
      }, 1500);
      return;
    }

    // Actualizar suscripción en local para reflejar estado activo
    try {
      const storedUser = JSON.parse(localStorage.getItem('vimo_user') || '{}');
      storedUser.subscription = { status: 'active', plan: 'suscripcion', expiresAt: '2027-12-31' };
      localStorage.setItem('vimo_user', JSON.stringify(storedUser));
    } catch {}

    toast({ message: '¡Datos bancarios verificados! Suscripción activada. Bienvenido a Vimo.', type: 'success' });
    setTimeout(() => { window.location.href = 'escritorio.html'; }, 1200);
  });
}
